import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Agent, WorkflowVersion } from "@/store/agentsStore";
import { Save, ChevronsUpDown, Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { getTimezoneList } from "@/components/cron/cronUtils";
import { DEFAULT_AGENT_TIMEZONE } from "@forgepoint/shared";

interface EditAgentDialogProps {
  agent: Agent;
  workflowVersion?: WorkflowVersion;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    name: string;
    description?: string;
    prompt?: string;
    timezone: string;
    loadRules: boolean;
  }) => Promise<void>;
  isSaving?: boolean;
}

export function EditAgentDialog({
  agent,
  workflowVersion,
  open,
  onOpenChange,
  onSave,
  isSaving = false,
}: EditAgentDialogProps) {
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description || "");
  const [prompt, setPrompt] = useState(workflowVersion?.prompt || "");
  const [timezone, setTimezone] = useState(
    workflowVersion?.timezone || DEFAULT_AGENT_TIMEZONE,
  );
  const [loadRules, setLoadRules] = useState(
    workflowVersion?.loadRules ?? false,
  );
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const timezoneOptions = useMemo(() => getTimezoneList(), []);

  useEffect(() => {
    if (open) {
      setName(agent.name);
      setDescription(agent.description || "");
      setPrompt(workflowVersion?.prompt || "");
      setTimezone(workflowVersion?.timezone || DEFAULT_AGENT_TIMEZONE);
      setLoadRules(workflowVersion?.loadRules ?? false);
    }
  }, [open, agent, workflowVersion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      name,
      description: description || undefined,
      prompt: prompt || undefined,
      timezone,
      loadRules,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Agent</DialogTitle>
            <DialogDescription>
              Update the agent name, description, system prompt, and timezone.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter agent name"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter agent description"
                className="min-h-[80px]"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="prompt">System Prompt</Label>
              <Textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter a custom system prompt for this agent (optional)"
                className="min-h-[120px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                This prompt will be appended to the agent's execution system
                prompt. It can be used to provide specific instructions or
                context for this agent.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={timezoneOpen}
                    className="w-full justify-between"
                  >
                    {timezone}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search timezone..." />
                    <CommandEmpty>No timezone found.</CommandEmpty>
                    <CommandList>
                      {timezoneOptions.map((tz) => (
                        <CommandItem
                          key={tz}
                          value={tz}
                          onSelect={(value) => {
                            setTimezone(value);
                            setTimezoneOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              timezone === tz ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {tz}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                Dates and times shown in workflow context will be interpreted
                using this timezone.
              </p>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                <Label htmlFor="loadRules">
                  Load Shop Rules into Agent Context
                </Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, shop rules will be included in the agent's
                  system prompt
                </p>
              </div>
              <Switch
                id="loadRules"
                checked={loadRules}
                onCheckedChange={setLoadRules}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Save className="mr-2 h-4 w-4 animate-pulse" />}
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
